import { createContext, useContext, useEffect, useState, useMemo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

type City = "brasilia" | "rio-de-janeiro" | "goianesia";

interface CityContextType {
  city: City;
  setCity: (city: City) => void;
  isRio: boolean;
  phone: string;
  phoneDisplay: string;
  whatsAppNumber: string;
  email: string;
  cityName: string;
  cityPrep: string;
  region: string;
  regionFull: string;
  addressLocality: string;
  addressRegion: string;
  /** URL prefix for city-specific routes: "" for Brasília, "/rio-de-janeiro" for Rio */
  urlPrefix: string;
}

const CityContext = createContext<CityContextType | undefined>(undefined);

const CITY_DATA: Record<City, Omit<CityContextType, "city" | "setCity" | "isRio" | "urlPrefix">> = {
  brasilia: {
    phone: "tel:+5561992938591",
    phoneDisplay: "(61) 99293-8591",
    whatsAppNumber: "5561992938591",
    email: "bsb@ultraservicos.com.br",
    cityName: "Brasília",
    cityPrep: "em Brasília",
    region: "DF",
    regionFull: "Distrito Federal",
    addressLocality: "Brasília",
    addressRegion: "DF",
  },
  "rio-de-janeiro": {
    phone: "tel:+5521968500754",
    phoneDisplay: "(21) 96850-0754",
    whatsAppNumber: "5521968500754",
    email: "hellengoes.usarj@gmail.com",
    cityName: "Rio de Janeiro",
    cityPrep: "no Rio de Janeiro",
    region: "RJ",
    regionFull: "Rio de Janeiro",
    addressLocality: "Rio de Janeiro",
    addressRegion: "RJ",
  },
  goianesia: {
    phone: "tel:+5562996468506",
    phoneDisplay: "(62) 99646-8506",
    whatsAppNumber: "5562996468506",
    email: "dedetizadorausagoianesia@gmail.com",
    cityName: "Goianésia",
    cityPrep: "em Goianésia",
    region: "GO",
    regionFull: "Goiás",
    addressLocality: "Goianésia",
    addressRegion: "GO",
  },
};

const STORAGE_KEY = "usa-city-preference";

const cityFromPath = (pathname: string): City | null => {
  if (pathname.startsWith("/rio-de-janeiro")) return "rio-de-janeiro";
  if (pathname.startsWith("/goianesia")) return "goianesia";
  if (pathname.startsWith("/brasilia")) return "brasilia";
  return null;
};

export const CityProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [savedCity, setSavedCity] = useState<City>(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as City | null;
    if (saved === "rio-de-janeiro") return "rio-de-janeiro";
    if (saved === "goianesia") return "goianesia";
    return "brasilia";
  });

  // City is derived synchronously from the URL so metadata never renders
  // with the wrong city (crawlers read the very first render).
  const city = cityFromPath(location.pathname) ?? savedCity;

  useEffect(() => {
    const fromPath = cityFromPath(location.pathname);
    if (fromPath) {
      setSavedCity(fromPath);
      localStorage.setItem(STORAGE_KEY, fromPath);
    }
    // For shared pages (/, /contato, /servicos/*, /blog, etc.), keep the saved city preference
  }, [location.pathname]);

  const setCity = (newCity: City) => {
    setSavedCity(newCity);
    localStorage.setItem(STORAGE_KEY, newCity);
  };

  const data = CITY_DATA[city];
  const urlPrefix = city === "rio-de-janeiro" ? "/rio-de-janeiro" : city === "goianesia" ? "/goianesia" : "";


  const value = useMemo(
    () => ({ city, setCity, isRio: city === "rio-de-janeiro", urlPrefix, ...data }),
    [city, data, urlPrefix]
  );

  return (
    <CityContext.Provider value={value}>
      {children}
    </CityContext.Provider>
  );
};

export const useCity = () => {
  const ctx = useContext(CityContext);
  if (!ctx) throw new Error("useCity must be used within CityProvider");
  return ctx;
};

export type { City };
